import React, { useState } from 'react';
import {
  CostFilter,
  CostDimension,
  FilterOperator,
} from '../../types/costExplorer';

/**
 * Filter Panel Component
 * Provides filtering capabilities for cost explorer
 * AC 3: Support filtering
 */
interface FilterPanelProps {
  filters: CostFilter[];
  onFiltersChange: (filters: CostFilter[]) => void;
  availableDimensions: CostDimension[];
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  availableDimensions,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const addFilter = () => {
    const newFilter: CostFilter = {
      dimension: availableDimensions[0],
      operator: FilterOperator.EQUALS,
      values: [],
    };
    onFiltersChange([...filters, newFilter]);
  };

  const removeFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    onFiltersChange(newFilters);
  };

  const updateFilter = (index: number, updates: Partial<CostFilter>) => {
    const newFilters = filters.map((filter, i) =>
      i === index ? { ...filter, ...updates } : filter
    );
    onFiltersChange(newFilters);
  };

  const getDimensionLabel = (dim: CostDimension): string => {
    const labels: Record<CostDimension, string> = {
      [CostDimension.CLOUD]: 'Cloud Provider',
      [CostDimension.ACCOUNT]: 'Account',
      [CostDimension.SERVICE]: 'Service',
      [CostDimension.REGION]: 'Region',
      [CostDimension.TAG]: 'Tag',
      [CostDimension.APPLICATION]: 'Application',
      [CostDimension.BUSINESS_UNIT]: 'Business Unit',
      [CostDimension.ENVIRONMENT]: 'Environment',
      [CostDimension.CUSTOM]: 'Custom Dimension',
    };
    return labels[dim];
  };

  const getOperatorLabel = (op: FilterOperator): string => {
    const labels: Record<FilterOperator, string> = {
      [FilterOperator.EQUALS]: 'Equals',
      [FilterOperator.NOT_EQUALS]: 'Not Equals',
      [FilterOperator.CONTAINS]: 'Contains',
      [FilterOperator.NOT_CONTAINS]: 'Not Contains',
      [FilterOperator.IN]: 'In',
      [FilterOperator.NOT_IN]: 'Not In',
      [FilterOperator.GREATER_THAN]: 'Greater Than',
      [FilterOperator.LESS_THAN]: 'Less Than',
      [FilterOperator.BETWEEN]: 'Between',
    };
    return labels[op];
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 dark:border-border dark:bg-card">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          Filters
          {filters.length > 0 && (
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary dark:bg-primary/10 dark:text-primary">
              {filters.length}
            </span>
          )}
        </h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-muted-foreground hover:text-foreground dark:text-muted-foreground/70 dark:hover:text-white"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
        >
          <svg
            className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          {filters.map((filter, index) => (
            <div
              key={index}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-3 dark:border-border"
            >
              {/* Dimension selector */}
              <select
                value={filter.dimension}
                onChange={(e) =>
                  updateFilter(index, { dimension: e.target.value as CostDimension })
                }
                className="rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                aria-label="Filter dimension"
              >
                {availableDimensions.map((dim) => (
                  <option key={dim} value={dim}>
                    {getDimensionLabel(dim)}
                  </option>
                ))}
              </select>

              {/* Operator selector */}
              <select
                value={filter.operator}
                onChange={(e) =>
                  updateFilter(index, { operator: e.target.value as FilterOperator })
                }
                className="rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                aria-label="Filter operator"
              >
                {Object.values(FilterOperator).map((op) => (
                  <option key={op} value={op}>
                    {getOperatorLabel(op)}
                  </option>
                ))}
              </select>

              {/* Value input */}
              <input
                type="text"
                value={filter.values.join(', ')}
                onChange={(e) =>
                  updateFilter(index, {
                    values: e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
                  })
                }
                placeholder="Enter values (comma-separated)"
                className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                aria-label="Filter values"
              />

              {/* Remove button */}
              <button
                onClick={() => removeFilter(index)}
                className="rounded-md p-2 text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20"
                aria-label="Remove filter"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}

          {/* Add filter button */}
          <button
            onClick={addFilter}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border px-4 py-2 text-sm font-medium text-foreground/80 hover:border-border hover:bg-muted/30 dark:border-border dark:text-muted-foreground dark:hover:border-border dark:hover:bg-secondary"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Filter
          </button>

          {filters.length > 0 && (
            <button
              onClick={() => onFiltersChange([])}
              className="w-full rounded-md px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20"
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};
